/**
 * Rax Automation Rules List
 *
 * Displays and manages user's automation rules.
 * Allows viewing, pausing, resuming, and deleting rules.
 */

import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { raxApi, type AutomationRule, type AutomationRuleStatus } from "../../api/rax-api.js";
import { raxTheme } from "../styles/rax-theme.js";

@customElement("rax-automation-rules-list")
export class RaxAutomationRulesList extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
      }
      
      .rules-container {
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
        overflow: hidden;
      }
      
      .rules-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-4);
        border-bottom: 1px solid var(--border-color);
        background: var(--bg-elevated);
      }
      
      .rules-header h2 {
        margin: 0;
        font-size: var(--font-lg);
        font-weight: 600;
        color: var(--text-primary);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      
      .rules-header h2::before {
        content: "\\26A1";
        font-size: var(--font-xl);
      }
      
      .rule-count {
        font-size: var(--font-sm);
        color: var(--text-tertiary);
        font-weight: normal;
      }
      
      .rules-list {
        display: flex;
        flex-direction: column;
      }
      
      .rule-item {
        display: flex;
        align-items: flex-start;
        gap: var(--space-4);
        padding: var(--space-4);
        border-bottom: 1px solid var(--border-color);
        transition: background var(--transition-fast);
      }
      
      .rule-item:last-child {
        border-bottom: none;
      }
      
      .rule-item:hover {
        background: var(--bg-hover);
      }
      
      .rule-item.paused {
        opacity: 0.6;
      }
      
      .rule-icon {
        width: 40px;
        height: 40px;
        border-radius: var(--radius-md);
        background: var(--bg-subtle);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: var(--font-xl);
        flex-shrink: 0;
      }
      
      .rule-icon.schedule {
        background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(59, 130, 246, 0.1));
        color: #3b82f6;
      }
      
      .rule-icon.event {
        background: linear-gradient(135deg, rgba(255, 208, 0, 0.2), rgba(255, 208, 0, 0.1));
        color: var(--rax-yellow);
      }
      
      .rule-content {
        flex: 1;
        min-width: 0;
      }
      
      .rule-name {
        font-size: var(--font-md);
        font-weight: 600;
        color: var(--text-primary);
        margin: 0 0 var(--space-1);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      
      .rule-status-badge {
        font-size: var(--font-xs);
        font-weight: 600;
        padding: 2px 6px;
        border-radius: var(--radius-sm);
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }
      
      .rule-status-badge.active {
        background: rgba(16, 185, 129, 0.2);
        color: #10b981;
      }
      
      .rule-status-badge.paused {
        background: rgba(245, 158, 11, 0.2);
        color: #f59e0b;
      }
      
      .rule-status-badge.disabled {
        background: rgba(107, 114, 128, 0.2);
        color: #6b7280;
      }
      
      .rule-trigger {
        font-size: var(--font-sm);
        color: var(--text-secondary);
        margin: 0 0 var(--space-2);
      }
      
      .rule-action {
        font-size: var(--font-sm);
        color: var(--text-tertiary);
        margin: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      
      .rule-meta {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
        margin-top: var(--space-2);
      }
      
      .rule-actions {
        display: flex;
        gap: var(--space-2);
        flex-shrink: 0;
      }
      
      .action-btn {
        background: transparent;
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        padding: var(--space-2);
        cursor: pointer;
        color: var(--text-tertiary);
        transition: all var(--transition-fast);
        font-size: var(--font-sm);
      }
      
      .action-btn:hover {
        border-color: var(--text-tertiary);
        color: var(--text-primary);
      }
      
      .action-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      
      .action-btn.danger:hover {
        border-color: var(--error);
        color: var(--error);
      }
      
      .empty-state {
        padding: var(--space-8);
        text-align: center;
        color: var(--text-tertiary);
      }
      
      .empty-state-icon {
        font-size: 3rem;
        margin-bottom: var(--space-4);
        opacity: 0.5;
      }
      
      .empty-state h3 {
        margin: 0 0 var(--space-2);
        font-size: var(--font-lg);
        color: var(--text-secondary);
      }
      
      .empty-state p {
        margin: 0;
        font-size: var(--font-sm);
      }
      
      .loading-state {
        padding: var(--space-8);
        text-align: center;
        color: var(--text-tertiary);
      }
      
      .error-state {
        padding: var(--space-4);
        background: rgba(239, 68, 68, 0.14);
        border: 1px solid rgba(239, 68, 68, 0.55);
        border-radius: var(--radius-md);
        color: #fca5a5;
        margin: var(--space-4);
      }
      
      @media (max-width: 640px) {
        .rule-item {
          flex-direction: column;
          gap: var(--space-3);
        }
      
        .rule-actions {
          width: 100%;
          justify-content: flex-end;
        }
      }
    `,
  ];

  @property({ type: String })
  userId: string = "";

  @state()
  private rules: AutomationRule[] = [];

  @state()
  private loading = true;

  @state()
  private error: string | null = null;

  @state()
  private actionInProgress: Record<string, boolean> = {};

  override connectedCallback() {
    super.connectedCallback();
    this.loadRules();
  }

  private async loadRules() {
    this.loading = true;
    this.error = null;

    try {
      const response = await raxApi.listAutomationRules();
      if (response.success && response.data) {
        this.rules = response.data;
      } else {
        this.error = response.error || "Failed to load automation rules";
      }
    } catch (err) {
      console.error("[AutomationRulesList] Error loading rules:", err);
      this.error = err instanceof Error ? err.message : "Failed to load rules";
    } finally {
      this.loading = false;
    }
  }

  override render() {
    return html`
      <div class="rules-container">
        <div class="rules-header">
          <h2>
            Automations
            ${
              this.rules.length > 0
                ? html`
              <span class="rule-count">(${this.rules.length})</span>
            `
                : nothing
            }
          </h2>
        </div>

        ${
          this.loading
            ? html`
                <div class="loading-state">Loading automations...</div>
              `
            : this.error
              ? html`
          <div class="error-state">${this.error}</div>
        `
              : this.rules.length === 0
                ? html`
                    <div class="empty-state">
                      <div class="empty-state-icon">&#9889;</div>
                      <h3>No automations yet</h3>
                      <p>
                        Create automations by describing what you want in chat, like "remind me every day at 9am to
                        check my tasks"
                      </p>
                    </div>
                  `
                : html`
          <div class="rules-list">
            ${this.rules.map((rule) => this.renderRule(rule))}
          </div>
        `
        }
      </div>
    `;
  }

  private renderRule(rule: AutomationRule) {
    const isSchedule = rule.triggerType === "schedule";
    const config = rule.triggerConfig as Record<string, unknown>;
    const triggerText = isSchedule
      ? (config.schedule as string) || "Scheduled"
      : (config.eventCondition as string) || "When triggered";
    const inProgress = this.actionInProgress[rule.id] ?? false;

    return html`
      <div class="rule-item ${rule.status === "paused" ? "paused" : ""}">
        <div class="rule-icon ${isSchedule ? "schedule" : "event"}">
          ${isSchedule ? "&#128337;" : "&#9889;"}
        </div>
        <div class="rule-content">
          <h3 class="rule-name">
            ${rule.name}
            <span class="rule-status-badge ${rule.status}">${rule.status}</span>
          </h3>
          <p class="rule-trigger">
            ${isSchedule ? "&#128337;" : "&#9889;"} ${triggerText}
          </p>
          <p class="rule-action">${rule.action}</p>
          <div class="rule-meta">
            Created ${this.formatDate(rule.createdAt)}
          </div>
        </div>
        <div class="rule-actions">
          ${
            rule.status === "active"
              ? html`
            <button
              class="action-btn"
              ?disabled=${inProgress}
              @click=${() => this.updateStatus(rule.id, "paused")}
              title="Pause automation"
            >
              &#10074;&#10074;
            </button>
          `
              : html`
            <button
              class="action-btn"
              ?disabled=${inProgress}
              @click=${() => this.updateStatus(rule.id, "active")}
              title="Resume automation"
            >
              &#9654;
            </button>
          `
          }
          <button
            class="action-btn danger"
            ?disabled=${inProgress}
            @click=${() => this.deleteRule(rule.id)}
            title="Delete automation"
          >
            &#128465;
          </button>
        </div>
      </div>
    `;
  }

  private formatDate(dateStr: string): string {
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return "today";
      } else if (diffDays === 1) {
        return "yesterday";
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else {
        return date.toLocaleDateString();
      }
    } catch {
      return dateStr;
    }
  }

  private async updateStatus(id: string, status: AutomationRuleStatus) {
    this.actionInProgress = { ...this.actionInProgress, [id]: true };

    try {
      const response = await raxApi.updateAutomationRuleStatus(id, status);
      if (response.success && response.data) {
        this.rules = this.rules.map((r) => (r.id === id ? response.data! : r));
        this.dispatchEvent(
          new CustomEvent("rule-updated", {
            detail: { rule: response.data },
          }),
        );
      } else {
        console.error("[AutomationRulesList] Failed to update status:", response.error);
      }
    } catch (err) {
      console.error("[AutomationRulesList] Error updating status:", err);
    } finally {
      this.actionInProgress = { ...this.actionInProgress, [id]: false };
    }
  }

  private async deleteRule(id: string) {
    if (!confirm("Delete this automation? This cannot be undone.")) {
      return;
    }

    this.actionInProgress = { ...this.actionInProgress, [id]: true };

    try {
      const response = await raxApi.deleteAutomationRule(id);
      if (response.success) {
        this.rules = this.rules.filter((r) => r.id !== id);
        this.dispatchEvent(
          new CustomEvent("rule-deleted", {
            detail: { id },
          }),
        );
      } else {
        console.error("[AutomationRulesList] Failed to delete rule:", response.error);
      }
    } catch (err) {
      console.error("[AutomationRulesList] Error deleting rule:", err);
    } finally {
      this.actionInProgress = { ...this.actionInProgress, [id]: false };
    }
  }

  /** Public method to refresh the rules list */
  public async refresh() {
    await this.loadRules();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-automation-rules-list": RaxAutomationRulesList;
  }
}
